// @flow

import { type CheckoutReceipts, type Receipt, type Instalments, type FxLatencyReceiptSource } from './types';

export const ONE_INSTALMENT: CheckoutReceipts = {
  main: {
    fx: {
      from: 'USD',
      to: 'BRL',
      rate: '4.1700',
    },
    amount_ext: [ 'USD', '100.00' ],
    amount_local_base: [ 'BRL', '417.00' ],
    amount_local_total: [ 'BRL', '418.58' ],
    local_taxes: [
      {
        code: 'BR_IOF',
        amount: [ 'BRL', '1.58' ],
        rate: '0.0038',
      },
    ],
  },
  instalments_strategy: 'from-invoice',
  instalments: {
    [1]: {
      fx: {
        from: 'USD',
        to: 'BRL',
        rate: '4.1700',
      },
      amount_ext: [ 'USD', '100.00' ],
      amount_local_base: [ 'BRL', '417.00' ],
      amount_local_total: [ 'BRL', '418.58' ],
      local_taxes: [
        {
          code: 'BR_IOF',
          amount: [ 'BRL', '1.58' ],
          rate: '0.0038',
        },
      ],
      instalments_plan: {
        instalments: 1,
        amount_partial_first: [ 'BRL', '418.58' ],
        amount_partial_other: [ 'BRL', '418.58' ],
      },
    },
  },
};

export const TWO_INSTALMENTS: CheckoutReceipts = {
  main: {
    fx: {
      from: 'USD',
      to: 'BRL',
      rate: '4.1700',
    },
    amount_ext: [ 'USD', '100.00' ],
    amount_local_base: [ 'BRL', '417.00' ],
    amount_local_total: [ 'BRL', '418.58' ],
    local_taxes: [
      {
        code: 'BR_IOF',
        amount: [ 'BRL', '1.58' ],
        rate: '0.0038',
      },
    ],
  },
  instalments_strategy: 'from-invoice',
  instalments: {
    [1]: {
      fx: {
        from: 'USD',
        to: 'BRL',
        rate: '4.1700',
      },
      amount_ext: [ 'USD', '100.00' ],
      amount_local_base: [ 'BRL', '417.00' ],
      amount_local_total: [ 'BRL', '418.58' ],
      local_taxes: [
        {
          code: 'BR_IOF',
          amount: [ 'BRL', '1.58' ],
          rate: '0.0038',
        },
      ],
      instalments_plan: {
        instalments: 1,
        amount_partial_first: [ 'BRL', '418.58' ],
        amount_partial_other: [ 'BRL', '418.58' ],
      },
    },
    [2]: {
      fx: {
        from: 'USD',
        to: 'BRL',
        rate: '4.1700',
      },
      amount_ext: [ 'USD', '100.00' ],
      amount_local_base: [ 'BRL', '417.00' ],
      amount_local_total: [ 'BRL', '418.58' ],
      local_taxes: [
        {
          code: 'BR_IOF',
          amount: [ 'BRL', '1.58' ],
          rate: '0.0038',
        },
      ],
      instalments_plan: {
        instalments: 2,
        amount_partial_first: [ 'BRL', '209.29' ],
        amount_partial_other: [ 'BRL', '209.29' ],
      },
    },
  },
};

export const TWO_INSTALMENTS_WITH_FEES: CheckoutReceipts = {
  main: {
    fx: {
      from: 'USD',
      to: 'BRL',
      rate: '4.1700',
    },
    amount_ext: [ 'USD', '100.00' ],
    amount_local_base: [ 'BRL', '417.00' ],
    amount_local_total: [ 'BRL', '418.58' ],
    local_taxes: [
      {
        code: 'BR_IOF',
        amount: [ 'BRL', '1.58' ],
        rate: '0.0038',
      },
    ],
  },
  instalments_strategy: 'from-invoice',
  instalments: {
    [1]: {
      fx: {
        from: 'USD',
        to: 'BRL',
        rate: '4.1700',
      },
      amount_ext: [ 'USD', '100.00' ],
      amount_local_base: [ 'BRL', '417.00' ],
      amount_local_total: [ 'BRL', '418.58' ],
      local_taxes: [
        {
          code: 'BR_IOF',
          amount: [ 'BRL', '1.58' ],
          rate: '0.0038',
        },
      ],
      instalments_plan: {
        instalments: 1,
        amount_partial_first: [ 'BRL', '418.58' ],
        amount_partial_other: [ 'BRL', '418.58' ],
      },
    },
    [2]: {
      fx: {
        from: 'USD',
        to: 'BRL',
        rate: '4.1700',
      },
      amount_ext: [ 'USD', '100.00' ],
      amount_local_base: [ 'BRL', '417.00' ],
      amount_local_total: [ 'BRL', '428.58' ],
      local_taxes: [
        {
          code: 'BR_IOF',
          amount: [ 'BRL', '1.58' ],
          rate: '0.0038',
        },
      ],
      instalments_plan: {
        instalments: 2,
        amount_partial_first: [ 'BRL', '214.29' ],
        amount_partial_other: [ 'BRL', '214.29' ],
      },
    },
  },
};

// Visa `payment_type` code used as an fx_latency key.
export const FX_LATENCY_PAYMENT_TYPE_CODE: string = 'visa';

// Boleto `payment_type` code used as an fx_latency key.
export const BOLETO_PAYMENT_TYPE_CODE: string = 'boleto';

// Buffered-rate receipt (rate 4.2100 vs the base 4.1700) used by the fx_latency fixtures.
const BUFFERED_RECEIPT: Receipt = {
  fx: {
    from: 'USD',
    to: 'BRL',
    rate: '4.2100',
  },
  amount_ext: [ 'USD', '100.00' ],
  amount_local_base: [ 'BRL', '421.00' ],
  amount_local_total: [ 'BRL', '422.60' ],
  local_taxes: [
    {
      code: 'BR_IOF',
      amount: [ 'BRL', '1.60' ],
      rate: '0.0038',
    },
  ],
};

function bufferedInstalment(instalments: number, partial: string): Receipt {
  return {
    ...BUFFERED_RECEIPT,
    instalments_plan: {
      instalments,
      amount_partial_first: [ 'BRL', partial ],
      amount_partial_other: [ 'BRL', partial ],
    },
  };
}

function bufferedSource(instalments: Instalments): FxLatencyReceiptSource {
  return { main: { ...BUFFERED_RECEIPT }, instalments };
}

export const TWO_INSTALMENTS_WITH_FX_LATENCY: CheckoutReceipts = {
  ...TWO_INSTALMENTS,
  fx_latency: {
    [FX_LATENCY_PAYMENT_TYPE_CODE]: bufferedSource({
      [1]: bufferedInstalment(1, '422.60'),
      [2]: bufferedInstalment(2, '211.30'),
    }),
  },
};

export const ONE_INSTALMENT_WITH_BOLETO_FX_LATENCY: CheckoutReceipts = {
  ...ONE_INSTALMENT,
  fx_latency: {
    [BOLETO_PAYMENT_TYPE_CODE]: bufferedSource({
      [1]: bufferedInstalment(1, '422.60'),
    }),
  },
};

export const DEBIN_INSTALMENT_FEE_BY_CUSTOMER: CheckoutReceipts = {
  main: {
    fx: {
      from: 'USD',
      to: 'BRL',
      rate: '4.1700',
    },
    amount_ext: [ 'USD', '100.00' ],
    amount_local_base: [ 'BRL', '417.00' ],
    amount_local_total: [ 'BRL', '418.58' ],
    local_taxes: [
      {
        code: 'BR_IOF',
        amount: [ 'BRL', '1.58' ],
        rate: '0.0038',
      },
    ],
  },
  instalments_strategy: 'from-invoice',
  instalments: {
    [1]: {
      fx: {
        from: 'USD',
        to: 'BRL',
        rate: '4.1700',
      },
      amount_ext: [ 'USD', '100.00' ],
      amount_local_base: [ 'BRL', '417.00' ],
      amount_local_total: [ 'BRL', '418.58' ],
      local_taxes: [
        {
          code: 'BR_IOF',
          amount: [ 'BRL', '1.58' ],
          rate: '0.0038',
        },
      ],
      instalments_plan: {
        instalments: 1,
        amount_partial_first: [ 'BRL', '418.58' ],
        amount_partial_other: [ 'BRL', '418.58' ],
      },
    },
  },
  instalments_with_end_user_fee: {
    banktransfer: {
      debin: {
        [1]: {
          transaction_fees: [
            {
              code: 'INSTALLMENT_FEE',
              amount: ['ARS', '29.00'],
              percent_value: '3.99',
            },
          ],
          fx: {
            from: 'ARS',
            to: 'ARS',
            rate: '1.0000',
          },
          amount_ext: ['ARS', '100.00'],
          amount_local_base: ['ARS', '129.00'],
          amount_local_total: ['ARS', '152.00'],
          local_taxes: [
            { code: 'AR_IG_IBP', amount: ['ARS', '0.00'], rate: '0.0000' },
            { code: 'AR_NON_RESIDENT_IGBP', amount: ['ARS', '1.00'], rate: '0.0100' },
            { code: 'AR_IIBB_DF', amount: ['ARS', '2.00'], rate: '0.0200' },
            { code: 'AR_IVA', amount: ['ARS', '21.00'], rate: '0.2100' },
            { code: 'INSTALLMENT_FEE', amount: ['ARS', '21.00'], rate: '0.2100' },
          ],
          installments_plan_token: 'fee-q3asdjahsdkja',
          instalments_plan: {
            instalments: 1,
            amount_partial_first: ['ARS', '152.00'],
            amount_partial_other: ['ARS', '152.00'],
          },
        },
      },
    },
  },
};
